import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { ko } from 'date-fns/locale';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const WorkCertificate: React.FC = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState<'week' | 'month'>('month');
  const [loading, setLoading] = useState(false);

  const generatePDF = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // 기간 설정
      const today = new Date();
      const startDate = period === 'week' 
        ? format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd')
        : format(startOfMonth(today), 'yyyy-MM-dd');
      const endDate = period === 'week'
        ? format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd')
        : format(endOfMonth(today), 'yyyy-MM-dd');

      // 데이터 조회
      const response = await api.get('/api/attendance/work-certificate', {
        params: { startDate, endDate },
      });

      const { user: userData, stats, records } = response.data.data;

      // HTML 템플릿 생성
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('팝업 차단을 해제해주세요');
        return;
      }

      const html = `
        <!DOCTYPE html>
        <html lang="ko">
        <head>
          <meta charset="UTF-8">
          <title>AZOCSII_근무확인서_${userData.name}_${startDate}_${endDate}</title>
          <style>
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
            body {
              font-family: 'Malgun Gothic', '맑은 고딕', 'Apple SD Gothic Neo', sans-serif;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
            }
            h1 {
              text-align: center;
              color: #ff8c00;
              margin-bottom: 5px;
            }
            h2 {
              text-align: center;
              color: #666;
              font-size: 18px;
              margin-top: 0;
              margin-bottom: 10px;
            }
            .subtitle {
              text-align: center;
              color: #999;
              font-size: 14px;
              margin-bottom: 30px;
            }
            .section {
              margin-bottom: 30px;
            }
            .section-title {
              font-size: 18px;
              font-weight: bold;
              color: #ff8c00;
              margin-bottom: 10px;
              border-bottom: 2px solid #ff8c00;
              padding-bottom: 5px;
            }
            .info-table, .stats-table, .records-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            .info-table td {
              padding: 8px;
              border: 1px solid #ddd;
            }
            .info-table td:first-child {
              background-color: #f8f8f8;
              font-weight: bold;
              width: 150px;
            }
            .stats-table th, .records-table th {
              background-color: #ff8c00;
              color: white;
              padding: 10px;
              text-align: left;
            }
            .stats-table td, .records-table td {
              padding: 8px;
              border: 1px solid #ddd;
            }
            .stats-table tr:nth-child(even), .records-table tr:nth-child(even) {
              background-color: #f8f8f8;
            }
            .late {
              color: red;
              font-weight: bold;
            }
            .footer {
              margin-top: 50px;
              text-align: center;
              color: #666;
            }
            .signature {
              margin-top: 20px;
              text-align: center;
            }
            .signature p {
              margin: 5px 0;
            }
            .print-btn {
              background-color: #ff8c00;
              color: white;
              border: none;
              padding: 10px 20px;
              font-size: 16px;
              border-radius: 5px;
              cursor: pointer;
              margin: 20px auto;
              display: block;
            }
            .print-btn:hover {
              background-color: #e67e00;
            }
          </style>
        </head>
        <body>
          <button class="print-btn no-print" onclick="window.print()">🖨️ 인쇄 / PDF 저장</button>
          
          <h1>근무 확인서</h1>
          <h2>AZOCSII Platform</h2>
          <div class="subtitle">발급일: ${format(new Date(), 'yyyy년 MM월 dd일')}</div>

          <div class="section">
            <div class="section-title">1. 기본 정보</div>
            <table class="info-table">
              <tr>
                <td>성명</td>
                <td>${userData.name}</td>
              </tr>
              <tr>
                <td>이메일</td>
                <td>${userData.email}</td>
              </tr>
              <tr>
                <td>조회 기간</td>
                <td>${startDate} ~ ${endDate}</td>
              </tr>
              <tr>
                <td>발급 사유</td>
                <td>근무 확인</td>
              </tr>
            </table>
          </div>

          <div class="section">
            <div class="section-title">2. 근무 통계</div>
            <table class="stats-table">
              <thead>
                <tr>
                  <th>항목</th>
                  <th>내용</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>총 근무일수</td><td>${stats.totalDays}일</td></tr>
                <tr><td>총 근무시간</td><td>${stats.totalHours.toFixed(1)}시간</td></tr>
                <tr><td>기본 근무시간</td><td>${stats.regularHours.toFixed(1)}시간</td></tr>
                <tr><td>초과 근무시간</td><td>${stats.overtimeHours.toFixed(1)}시간</td></tr>
                <tr><td>야간 근무시간</td><td>${stats.nightHours.toFixed(1)}시간</td></tr>
                <tr><td>지각</td><td>${stats.lateCount}회</td></tr>
                <tr><td>조퇴</td><td>${stats.earlyLeaveCount}회</td></tr>
                <tr><td>결근</td><td>${stats.absentCount}회</td></tr>
              </tbody>
            </table>
          </div>

          <div class="section">
            <div class="section-title">3. 상세 출근부</div>
            <table class="records-table">
              <thead>
                <tr>
                  <th>날짜</th>
                  <th>구분</th>
                  <th>출근</th>
                  <th>퇴근</th>
                  <th>근무시간</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                ${records.map((r: any) => `
                  <tr>
                    <td>${format(new Date(r.workDate), 'MM/dd (EEE)', { locale: ko })}</td>
                    <td>${r.sequence === 1 ? '기본근무' : `OT ${r.sequence - 1}차`}</td>
                    <td>${format(new Date(r.checkIn), 'HH:mm')}</td>
                    <td>${r.checkOut ? format(new Date(r.checkOut), 'HH:mm') : '-'}</td>
                    <td>${r.totalHours.toFixed(1)}시간</td>
                    <td class="${r.isLate || r.isEarlyLeave ? 'late' : ''}">
                      ${r.isLate ? '지각' : r.isEarlyLeave ? '조퇴' : '정상'}
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <div class="footer">
            <div class="signature">
              <p>위 내용이 사실임을 확인합니다.</p>
              <p>${format(new Date(), 'yyyy년 MM월 dd일')}</p>
              <p style="font-size: 18px; font-weight: bold; color: #ff8c00;">AZOCSII Platform</p>
            </div>
            <p style="margin-top: 30px; font-size: 12px; color: #999;">
              [ 본 문서는 AZOCSII 플랫폼에서 자동 생성되었습니다 ]
            </p>
          </div>

          <button class="print-btn no-print" onclick="window.print()">🖨️ 인쇄 / PDF 저장</button>
        </body>
        </html>
      `;

      printWindow.document.write(html);
      printWindow.document.close();

      toast.success('새 창에서 인쇄 화면이 열렸습니다');
    } catch (error: any) {
      console.error('PDF 생성 실패:', error);
      toast.error(error.response?.data?.message || 'PDF 생성 실패');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card-azocsii p-4">
      <h3 className="text-base font-bold text-primary mb-3">📄 근무 확인서</h3>
      <p className="text-xs text-gray-600 mb-3">
        본인의 근무 기록을 PDF로 저장할 수 있습니다.
      </p>

      {/* 기간 선택 */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-700 mb-1">조회 기간</label>
        <div className="flex gap-2">
          <button
            onClick={() => setPeriod('week')}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium ${
              period === 'week'
                ? 'bg-accent text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            이번 주
          </button>
          <button
            onClick={() => setPeriod('month')}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium ${
              period === 'month'
                ? 'bg-accent text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            이번 달
          </button>
        </div>
      </div>

      {/* 다운로드 버튼 */}
      <button
        onClick={generatePDF}
        disabled={loading}
        className="w-full btn-azocsii py-2.5 text-sm disabled:opacity-50"
      >
        {loading ? '⏳ 생성 중...' : '🖨️ 인쇄 / PDF 저장'}
      </button>

      {/* 안내 */}
      <div className="mt-3 p-2 bg-blue-50 rounded-lg">
        <p className="text-xs text-blue-700">
          💡 <strong>사용 방법:</strong>
          <br />
          1. 기간 선택 (이번 주 / 이번 달)
          <br />
          2. "인쇄 / PDF 저장" 클릭
          <br />
          3. 인쇄 대화상자에서 "PDF로 저장" 선택
          <br />
          4. 파일 다운로드 완료!
        </p>
      </div>
    </div>
  );
};

export default WorkCertificate;
